import { KitAlerts } from "@/components/pnl/KitAlerts";
import { MvaAlerts } from "@/components/MvaAlerts";
import { useAuth } from "@/context/AuthContext";
import { UnplannedLines } from "@/components/UnplannedLines";
import { ShiftPlanSplit } from "@/components/ShiftPlanSplit";
import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { MtdKpis, MtdTrend, MtdControls, useMtd } from "@/components/MtdTrend";
import { RefreshCw, AlertTriangle } from "lucide-react";
import api from "@/lib/api";
import { useMasters } from "@/hooks/useMasters";
import { FilterBar } from "@/components/FilterBar";
import { KpiCard } from "@/components/KpiCard";
import { AskAi } from "@/components/AskAi";
import { TrendChart } from "@/components/TrendChart";
import { LineMatrix } from "@/components/LineMatrix";
import { MvaSectionChart, MvaProjectTable } from "@/components/MvaCharts";
import { MvaTargetEditor, MvaGapTable } from "@/components/MvaTargets";
import { MvaTrend } from "@/components/MvaTrend";
import { ExportButton } from "@/components/ExportButton";
import { OnlineUsers } from "@/components/OnlineUsers";
import { useDispatchDashboard, DispatchKpi, DemandTracker, TrcKpi, UnplannedKpi, ModelCompare, MarginAlerts } from "@/components/DispatchWidgets";
import { KaizenOfMonth } from "@/components/KaizenOfMonth";
import { StatusBadge } from "@/components/StatusBadge";
import { fmtNum, fmtPct, todayISO } from "@/lib/format";

const clean = (f) => Object.fromEntries(Object.entries(f).filter(([, v]) => v !== "" && v != null));

function Breakdown({ title, items, valueKey, unit, testId }) {
  const max = Math.max(...items.map((i) => i[valueKey]), 1);
  return (
    <div className="pp-card p-5" data-testid={testId}>
          <div className="pp-label mb-3">{title}</div>
          {!items.length && <div className="text-sm text-slate-9000">No data for current filters</div>}
      <div className="space-y-2.5">
        {items.slice(0, 8).map((i) => (
          <div key={i.reason}>
            <div className="flex justify-between text-xs mb-1"><span className="text-slate-700">{i.reason}</span><span className="font-mono text-slate-500">{i[valueKey]}{unit}</span></div>
            <div className="h-1.5 rounded bg-slate-100"><div className="h-1.5 rounded bg-amber-500" style={{ width: `${(i[valueKey] / max) * 100}%` }} /></div>
          </div>
        ))}
      </div>
    </div>
  );
}

export default function Dashboard() {
  const { masters } = useMasters();
  const { user } = useAuth();
  const [filters, setFilters] = useState({ reporting_date: todayISO(), plant_id: "", section_id: "", line_id: "", shift_id: "" });
  const [auto, setAuto] = useState(true);
  const q = useQuery({
    queryKey: ["dashboard", filters],
    queryFn: async () => (await api.get("/dashboard", { params: clean(filters) })).data,
    refetchInterval: auto ? 60_000 : false,
  });
  useEffect(() => { if (masters.plants.length && !filters.plant_id) setFilters((f) => ({ ...f, plant_id: masters.plants[0].id })); }, [masters.plants]); // eslint-disable-line

  const d = q.data;
  const lossQ = useQuery({ queryKey: ["loss-today", filters.reporting_date], queryFn: async () => (await api.get("/production-loss/today", { params: { reporting_date: filters.reporting_date } })).data, refetchInterval: auto ? 60_000 : false });
  const L = lossQ.data;
  const s = d?.summary;
  const dq = useDispatchDashboard(filters.reporting_date, filters.plant_id);
  const [mode, setMode] = useState("day");
  const [mtdMonth, setMtdMonth] = useState("");
  const [compare, setCompare] = useState(true);
  const mtd = useMtd(filters, mtdMonth);
  const M = mode === "day" && !mtdMonth ? mtd.data?.summary : undefined;
  const prevMonth = (() => { const m = mtdMonth || filters.reporting_date.slice(0, 7); const [y, mo] = m.split("-").map(Number); const dte = new Date(y, mo - 2, 1); return `${dte.getFullYear()}-${String(dte.getMonth() + 1).padStart(2, "0")}`; })();
  const prev = useMtd(filters, prevMonth, compare);
  const pickDay = (date) => { if (date) { setFilters((f) => ({ ...f, reporting_date: date })); setMode("day"); window.scrollTo({ top: 0, behavior: "smooth" }); } };
  return (
    <div className="space-y-6" data-testid="dashboard-page">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="pp-label text-blue-400">Live performance</div>
          <h1 className="font-display text-3xl sm:text-4xl font-extrabold tracking-tight">Management Dashboard</h1>
        </div>
        <div className="flex items-center gap-3">
          <div className="inline-flex rounded-full bg-slate-100 p-0.5 text-xs font-semibold" data-testid="dashboard-mode">{[["day", "Day"], ["mtd", "MTD"]].map(([k, l]) => <button key={k} type="button" className={`rounded-full px-3 py-1.5 ${mode === k ? "bg-slate-900 text-white" : "text-slate-600 hover:bg-slate-200"}`} onClick={() => setMode(k)} data-testid={`dashboard-mode-${k}`}>{l}</button>)}</div>
          <label className="flex items-center gap-2 text-xs text-slate-500 cursor-pointer"><input type="checkbox" checked={auto} onChange={(e) => setAuto(e.target.checked)} data-testid="auto-refresh-toggle" className="accent-blue-500" /> Auto-refresh 60s</label>
          <button className="pp-btn-ghost h-9 px-3" onClick={() => q.refetch()} data-testid="dashboard-refresh-btn"><RefreshCw size={14} className={q.isFetching ? "animate-spin" : ""} /> Refresh</button>
          <ExportButton reportingDate={filters.reporting_date} plantId={filters.plant_id} />
          <AskAi context="dashboard" params={filters} hint={`Dashboard · ${filters.reporting_date} · productivity, quality & loss issues`} />
        </div>
      </div>

      <FilterBar masters={masters} filters={filters} setFilters={setFilters} showHour={false} />

      <OnlineUsers />
      <KitAlerts compact />
      <MvaAlerts date={filters.reporting_date} canEdit={["master_admin", "admin", "mis"].includes(user?.role)} compact />
      <UnplannedLines date={filters.reporting_date} plantId={filters.plant_id} sectionId={filters.section_id} canEdit={false} onPlan={() => {}} />
      {mode !== "mtd" && <ShiftPlanSplit date={filters.reporting_date} plantId={filters.plant_id} />}

      {s && s.missing > 0 && (
        <div className="flex items-center gap-3 rounded-md border border-rose-500/40 bg-rose-50 px-4 py-3 text-sm text-rose-800 rise" data-testid="missing-alert">
          <AlertTriangle size={18} className="text-rose-600 shrink-0" />
          <span><strong className="font-mono">{s.missing}</strong> of <strong className="font-mono">{s.expected}</strong> line(s) have no daily production report yet — flagged as MISSING, not auto-filled.</span>
        </div>
      )}

      {mode === "mtd" && <div className="flex flex-wrap items-center justify-between gap-3 rounded-md border border-blue-200 bg-blue-50 px-4 py-2 text-xs text-blue-900" data-testid="mtd-banner"><span>Showing <b>{mtdMonth ? "full month" : "month-to-date"}</b> figures {mtd.data ? `${mtd.data.from} → ${mtd.data.to}` : ""} for the selected plant / section / line. Pick another month to review history, or click any bar below to jump to that day.</span><MtdControls month={mtdMonth} setMonth={setMtdMonth} filters={filters} /></div>}
      {mode === "mtd" ? <MtdKpis s={mtd.data?.summary} /> : <div className="pp-kpi-grid-10">
        <KpiCard testId="kpi-production" title="Target vs Actual" status={s?.status} delay={1}
          value={s ? fmtNum(s.actual) : "—"} unit={s ? `/ ${fmtNum(s.target)}` : ""} mtd={M ? `${fmtNum(M.actual)} / ${fmtNum(M.target)} · ${fmtPct(M.achievement_pct)}` : undefined} sub={s ? `Achievement ${fmtPct(s.achievement_pct)} · ${s.records} records` : ""} />
        {s?.day_plan && <KpiCard testId="kpi-day-plan" title="Day Plan vs Actual" status={s.day_plan_status || undefined} delay={1}
          value={fmtNum(s.day_plan_actual)} unit={`/ ${fmtNum(s.day_plan)}`} mtd={M ? `${fmtNum(M.actual)} / ${fmtNum(M.day_plan)} · ${M.day_plan_pct != null ? fmtPct(M.day_plan_pct) : "—"}` : undefined} sub={`Daily Plan · ${s.day_plan_pct != null ? fmtPct(s.day_plan_pct) : "—"} · ${s.day_plan_lines} line(s) planned`} />}
        <KpiCard testId="kpi-oee" title="OEE" delay={2} status={s?.records ? (s.oee >= 85 ? "green" : s.oee >= 70 ? "amber" : "red") : undefined}
          value={s?.records ? fmtPct(s.oee) : "—"} mtd={M ? (M.records ? fmtPct(M.oee) : "—") : undefined} sub="Overall Equipment Effectiveness (avg of reported rows)" />
        <KpiCard testId="kpi-fpy" title="FPY" delay={3} status={s?.records ? (s.fpy >= 98 ? "green" : s.fpy >= 95 ? "amber" : "red") : undefined}
          value={s?.records ? fmtPct(s.fpy) : "—"} mtd={M ? (M.records ? fmtPct(M.fpy) : "—") : undefined} sub="First Pass Yield (avg of reported rows)" />
        <KpiCard testId="kpi-downtime" title="Downtime" delay={4} value={s ? fmtNum(s.downtime_min) : "—"} unit="min" mtd={M ? `${fmtNum(M.downtime_min)} min` : undefined}
          sub={d?.downtime_breakdown?.[0] ? `Top: ${d.downtime_breakdown[0].reason} (${d.downtime_breakdown[0].minutes}m)` : "No downtime logged"} />
        <KpiCard testId="kpi-defects" title="Defects" delay={5} value={s ? fmtNum(s.defects) : "—"} unit={s ? `· ${s.defect_rate}%` : ""} mtd={M ? `${fmtNum(M.defects)} · ${M.defect_rate}%` : undefined}
          sub={d?.defect_breakdown?.[0] ? `Top: ${d.defect_breakdown[0].reason} (${d.defect_breakdown[0].count})` : "Defect rate vs actual output"} />
        <KpiCard testId="kpi-ot" title="Overtime" delay={5} value={s ? `${s.ot_records}/${s.ot_hours_planned}` : "—"} unit="OT hrs"
          sub={s ? `${fmtNum(s.ot_actual)} units in OT · manpower on shift ${fmtNum(s.manpower)}` : ""} />
        <KpiCard testId="kpi-mva" title="MVA Earned" delay={5} value={s ? `₹${fmtNum(Math.round(s.mva_earned))}` : "—"} status={s?.mva_target ? (s.mva_earned / s.mva_target >= 0.95 ? "green" : s.mva_earned / s.mva_target >= 0.8 ? "amber" : "red") : undefined} mtd={M ? `₹${fmtNum(Math.round(M.mva_earned))}${M.mva_target ? ` · ${fmtPct((M.mva_earned / M.mva_target) * 100)} of target` : ""}` : undefined}
          sub={s?.mva_target ? `Target ₹${fmtNum(Math.round(s.mva_target))} · ${fmtPct((s.mva_earned / s.mva_target) * 100)} of MVA target` : "Actual output × project MVA per unit"} />
        <Link to="/production-loss" className="block" data-testid="kpi-loss-link"><KpiCard testId="kpi-loss" title="DA / DP Loss vs MVA" delay={5} value={L ? `₹${fmtNum(Math.round(L.cost))}` : "—"} unit={L && s?.mva_earned ? `· ${fmtPct((L.cost / s.mva_earned) * 100)} of MVA` : ""} status={L ? (L.cost === 0 ? "green" : s?.mva_earned && L.cost / s.mva_earned > 0.05 ? "red" : "amber") : undefined} mtd={L ? `₹${fmtNum(Math.round(L.mtd_cost))}${M?.mva_earned ? ` · ${fmtPct((L.mtd_cost / M.mva_earned) * 100)} of MTD MVA` : ""}` : undefined}
          sub={L ? `DA ₹${fmtNum(Math.round(L.da_cost))} · DP ₹${fmtNum(Math.round(L.dp_cost))}${s?.mva_earned ? ` · margin hit ${fmtPct((L.cost / s.mva_earned) * 100)}` : ""}` : "Part defects (DA) + process loss (DP) as % of MVA earned"} /></Link>
        <DispatchKpi data={dq.data} />
        <TrcKpi reportingDate={filters.reporting_date} plantId={filters.plant_id} />
        <UnplannedKpi reportingDate={filters.reporting_date} plantId={filters.plant_id} />
      </div>}
      <MtdTrend d={mtd.data} prev={prev.data} compare={compare} setCompare={setCompare} onPick={pickDay} />

      <ModelCompare reportingDate={filters.reporting_date} plantId={filters.plant_id} />
      <MarginAlerts />
      <DemandTracker data={dq.data} />
      <KaizenOfMonth month={filters.reporting_date?.slice(0, 7)} />

      <div className="grid grid-cols-1 xl:grid-cols-3 gap-4">
        <div className="pp-card p-5 xl:col-span-2 rise rise-2" data-testid="mva-section-card">
          <div className="flex items-center justify-between mb-3">
            <div>
              <div className="pp-label">MVA by section</div>
              <div className="text-sm text-slate-500">Earned vs target (dashed line) · ₹ for the selected day/filters{s?.daily_mva_target ? ` · Daily target ₹${fmtNum(Math.round(s.daily_mva_target))}` : ""}</div>
            </div>
            <MvaTargetEditor masters={masters} plantId={filters.plant_id} reportingDate={filters.reporting_date} />
          </div>
          {d ? <MvaSectionChart data={d.mva_by_section} /> : <div className="h-64 animate-pulse rounded bg-slate-100" />}
          {d && <MvaGapTable rows={d.mva_by_section} />}
        </div>
        <div className="pp-card p-5 rise rise-3" data-testid="mva-project-card">
          <div className="pp-label mb-3">MVA by project</div>
          {d ? <MvaProjectTable data={d.mva_by_project} /> : <div className="h-64 animate-pulse rounded bg-slate-100" />}
        </div>
      </div>

      <MvaTrend plantId={filters.plant_id} reportingDate={filters.reporting_date} />

      <div className="grid grid-cols-1 xl:grid-cols-3 gap-4">
        <div className="pp-card p-5 xl:col-span-2 rise rise-2">
          <div className="flex items-center justify-between mb-3">
            <div>
              <div className="pp-label">Line-wise day performance</div>
              <div className="text-sm text-slate-500">Target vs Produced per line (bar colour = status) · Downtime line</div>
            </div>
            <div className="flex gap-2 flex-wrap justify-end">
              <StatusBadge status="green" label="≥95%" /><StatusBadge status="amber" label="80–94%" /><StatusBadge status="red" label="<80%" /><StatusBadge status="missing" label="Missing" />
            </div>
          </div>
          {d ? <TrendChart data={d.trend} /> : <div className="h-72 animate-pulse rounded bg-slate-100" />}
          {d && (
            <div className="mt-3 flex flex-wrap gap-1.5" data-testid="line-strip">
              {d.trend.map((t, i) => (
                <span key={`${t.label}-${i}`} data-testid={`line-chip-${t.label}`} className={`rounded border px-2 py-0.5 font-mono text-[11px] status-${t.status}`} title={`${t.label}: ${t.status}`}>{t.label}{t.missing > 0 ? " ⚑" : ""}</span>
              ))}
            </div>
          )}
        </div>
        <div className="space-y-4">
          <Breakdown title="Defect reasons" items={d?.defect_breakdown || []} valueKey="count" unit="" testId="defect-breakdown" />
          <Breakdown title="Downtime reasons" items={d?.downtime_breakdown || []} valueKey="minutes" unit="m" testId="downtime-breakdown" />
        </div>
      </div>

      <div className="pp-card p-5 rise rise-3">
        <div className="flex items-center justify-between mb-3">
          <div>
            <div className="pp-label">Line comparison</div>
            <div className="text-sm text-slate-500">{d?.lines_count ?? 0} lines · {d?.reporting_date}</div>
          </div>
        </div>
        {d ? <LineMatrix lines={d.lines} reportingDate={filters.reporting_date} /> : <div className="h-40 animate-pulse rounded bg-slate-100" />}
      </div>
    </div>
  );
}
